import React from 'react';
import cx from 'classnames';
import isAbsoluteUrl from 'ha/utils/isAbsoluteUrl';
import { useIntl } from 'ha/i18n';

const isAnchorRef = (ref: string) => /^#/.test(ref);

export interface Props {
  to: string;
  className?: string;
  target?: '_blank' | '_self';
  title?: string;
  noFollow?: boolean;
  skipLangFormat?: boolean;
  rel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  linkRef?: (r: HTMLAnchorElement) => void;
  itemScope?: boolean;
  itemType?: string;
  component?: React.ElementType;
  children?: React.ReactNode;
}

const Link: React.FC<Props> = ({
  to = '/',
  children,
  noFollow,
  className,
  target,
  skipLangFormat,
  linkRef: ref,
  component,
  ...rest
}) => {
  const { urlResolver } = useIntl();
  const isTargetBlank = target === '_blank';

  const props = {
    className,
    target,
    ref,
    rel: cx({
      nofollow: noFollow,
      noopener: isTargetBlank,
      noreferrer: isTargetBlank,
    }),
    ...rest,
  };

  const getHref =
    isAnchorRef(to) || isAbsoluteUrl(to) || skipLangFormat
      ? to
      : urlResolver.formatHrefURL(to);

  if (component) {
    const LinkComponent = component;

    return (
      <LinkComponent to={getHref} {...props}>
        {children}
      </LinkComponent>
    );
  }

  return (
    <a href={getHref} {...props}>
      {children}
    </a>
  );
};

export default Link;
